import { AlertTriangleIcon, CopyIcon, FileSpreadsheetIcon, UploadIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  DuplicateReason,
  ImportOutcome,
  importCustomers,
  type DuplicateCandidate,
  type ImportReport,
  type ImportResult,
} from '@/api/customerTransfer'
import { useApiErrorMessage } from '@/api/errors'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { OdooExportGuide } from '@/components/customers/OdooExportGuide'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PHONE_FULL_SCREEN_DIALOG } from '@/components/layout/dialogStyles'
import { cn } from '@/lib/utils'

const OUTCOME_LABEL_KEYS = {
  [ImportOutcome.CREATED]: 'customerTransfer.outcome.CREATED',
  [ImportOutcome.UPDATED]: 'customerTransfer.outcome.UPDATED',
  [ImportOutcome.SKIPPED]: 'customerTransfer.outcome.SKIPPED',
} as const

const REASON_LABEL_KEYS = {
  [DuplicateReason.SAME_NAME]: 'customerTransfer.duplicates.reasons.SAME_NAME',
  [DuplicateReason.SIMILAR_NAME]: 'customerTransfer.duplicates.reasons.SIMILAR_NAME',
  [DuplicateReason.EMAIL_DOMAIN]: 'customerTransfer.duplicates.reasons.EMAIL_DOMAIN',
  [DuplicateReason.WEBSITE]: 'customerTransfer.duplicates.reasons.WEBSITE',
  [DuplicateReason.PHONE]: 'customerTransfer.duplicates.reasons.PHONE',
  [DuplicateReason.ADDRESS]: 'customerTransfer.duplicates.reasons.ADDRESS',
} as const

/** The excel mime type next to the extension: some systems hand a picker one, some the other. */
const ACCEPTED_FILES =
  '.csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/**
 * Reading a spreadsheet back into the customer master data.
 *
 * Always in two steps, and that is the whole design: choosing the file asks the backend what
 * *would* happen and shows it, and only a second, deliberate click writes anything. An import can
 * rewrite hundreds of records, and "I did not know it would do that" has to be impossible to say.
 *
 * The preview is also where duplicates are caught: for a company it would create, it names the
 * customers here it may already be, and somebody chooses — create it, or merge it into one of
 * them. Creating stays the default; a merge is never made without somebody picking it.
 */
export function CustomerImportDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** After something was actually written — the list behind this is stale from that moment. */
  onImported: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const input = useRef<HTMLInputElement>(null)
  /**
   * Which request the answer on screen is allowed to come from.
   *
   * Neither call can be cancelled — one of them may be halfway through writing — so the guard is
   * on the answer rather than the request. Without it, picking a second file while the first is
   * still in flight lets the first report land on top of the second.
   */
  const generation = useRef(0)

  const [file, setFile] = useState<File | null>(null)
  const [report, setReport] = useState<ImportReport | null>(null)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  /** The line of a company the preview would create, to the customer chosen to merge it into. */
  const [merges, setMerges] = useState<Record<number, string>>({})

  function reset() {
    // Whatever is in flight belongs to a dialog nobody is looking at any more.
    generation.current += 1
    setFile(null)
    setReport(null)
    setFailure(null)
    setMerges({})
    setBusy(false)
    if (input.current) {
      input.current.value = ''
    }
  }

  async function run(chosen: File, dryRun: boolean) {
    const mine = ++generation.current
    setBusy(true)
    setFailure(null)
    try {
      // Only with the write: a preview shows the candidates; what was chosen from them changes
      // what is written, not what the preview says.
      const result = await importCustomers(chosen, dryRun, dryRun ? {} : merges)
      if (generation.current !== mine) {
        return
      }
      setReport(result)
      if (result.applied) {
        toast.success(
          t('customerTransfer.applied', { created: result.created, updated: result.updated }),
        )
        onImported()
      }
    } catch (error) {
      if (generation.current === mine) {
        setFailure(errorMessage(error, t('customerTransfer.failed')))
      }
    } finally {
      if (generation.current === mine) {
        setBusy(false)
      }
    }
  }

  function choose(chosen: File) {
    setFile(chosen)
    setReport(null)
    // Keyed by line, and a line of another file is another company.
    setMerges({})
    void run(chosen, true)
  }

  const refused = report !== null && report.problems.length > 0
  const previewed = report !== null && !report.applied && !refused
  const changes = (report?.created ?? 0) + (report?.updated ?? 0)
  const suspected = previewed
    ? report.results.filter((result) => result.candidates.length > 0).length
    : 0
  const merging = Object.keys(merges).length

  function decide(line: number, customerId: string | null) {
    setMerges((current) => {
      const next = { ...current }
      if (customerId === null) {
        delete next[line]
      } else {
        next[line] = customerId
      }
      return next
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Not while a request is out. A dry run can be waited for; an apply is writing, and
        // closing over it would leave somebody with no idea whether it finished.
        if (busy) {
          return
        }
        if (!next) {
          reset()
        }
        onOpenChange(next)
      }}
    >
      <DialogContent
        className={cn('max-h-[90vh] overflow-y-auto sm:max-w-3xl', PHONE_FULL_SCREEN_DIALOG)}
        showCloseButton={!busy}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onPointerDownOutside={(event) => busy && event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('customerTransfer.importTitle')}</DialogTitle>
          <DialogDescription>{t('customerTransfer.importDescription')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" disabled={busy} onClick={() => input.current?.click()}>
              <FileSpreadsheetIcon />
              {file ? t('customerTransfer.chooseAnother') : t('customerTransfer.chooseFile')}
            </Button>
            {file && <span className="text-muted-foreground text-sm">{file.name}</span>}
            <input
              ref={input}
              type="file"
              accept={ACCEPTED_FILES}
              className="hidden"
              aria-label={t('customerTransfer.chooseFile')}
              onChange={(event) => {
                const chosen = event.target.files?.[0]
                if (chosen) {
                  choose(chosen)
                }
              }}
            />
          </div>

          <OdooExportGuide />

          {failure && (
            <Alert variant="destructive">
              <AlertDescription>{failure}</AlertDescription>
            </Alert>
          )}

          {refused && (
            <Alert variant="destructive">
              <AlertTriangleIcon />
              <AlertTitle>{t('customerTransfer.refused')}</AlertTitle>
              <AlertDescription>
                <p>{t('customerTransfer.refusedHint')}</p>
                <ul className="mt-2 grid gap-1">
                  {report.problems.map((problem) => (
                    <li key={`${problem.line}-${problem.message}`}>
                      {t('customerTransfer.line', { line: problem.line })}: {problem.message}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {report && !refused && (
            <>
              <p className="text-sm">
                {report.applied
                  ? t('customerTransfer.appliedSummary', {
                      created: report.created,
                      updated: report.updated,
                      skipped: report.skipped,
                    })
                  : t('customerTransfer.previewSummary', {
                      created: report.created,
                      updated: report.updated,
                      skipped: report.skipped,
                    })}
              </p>
              {suspected > 0 && (
                <Alert className="text-amber-600 dark:text-amber-500">
                  <CopyIcon />
                  <AlertDescription>
                    {t('customerTransfer.duplicates.notice', { count: suspected })}
                  </AlertDescription>
                </Alert>
              )}
              {previewed && merging > 0 && (
                <p className="text-sm">{t('customerTransfer.duplicates.mergeSummary', { count: merging })}</p>
              )}
              <ResultTable
                results={report.results}
                merges={previewed ? merges : {}}
                decidable={previewed && !busy}
                onDecide={decide}
              />
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            {report?.applied ? t('common.close') : t('common.cancel')}
          </Button>
          {/* Only once there is a preview with something in it to apply. Nothing here writes by
              accident: the button does not exist until the report says what it would do. */}
          {previewed && changes > 0 && (
            <Button type="button" disabled={busy} onClick={() => file && void run(file, false)}>
              <UploadIcon />
              {t('customerTransfer.apply', { count: changes })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ResultTable({
  results,
  merges,
  decidable,
  onDecide,
}: {
  results: ImportResult[]
  merges: Record<number, string>
  /** Only while there is a preview to decide on and nothing is being written. */
  decidable: boolean
  onDecide: (line: number, customerId: string | null) => void
}) {
  const { t } = useTranslation()

  if (results.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('customerTransfer.nothingToDo')}</p>
  }

  return (
    <div className="max-h-72 overflow-y-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">{t('customerTransfer.columns.line')}</TableHead>
            <TableHead>{t('customerTransfer.columns.company')}</TableHead>
            <TableHead>{t('customerTransfer.columns.outcome')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.map((result) => (
            <TableRow key={`${result.line}-${result.companyName}`}>
              <TableCell className="text-muted-foreground tabular-nums">{result.line}</TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span>{result.companyName}</span>
                  {result.detail && (
                    <span className="text-muted-foreground text-xs">{result.detail}</span>
                  )}
                  {result.candidates.length > 0 && (
                    <DuplicateChoice
                      result={result}
                      chosen={merges[result.line] ?? null}
                      disabled={!decidable}
                      onDecide={(customerId) => onDecide(result.line, customerId)}
                    />
                  )}
                </div>
              </TableCell>
              <TableCell>
                {merges[result.line] ? (
                  <Badge variant="secondary">{t('customerTransfer.duplicates.merging')}</Badge>
                ) : (
                  <Badge variant={result.outcome === ImportOutcome.SKIPPED ? 'outline' : 'secondary'}>
                    {t(OUTCOME_LABEL_KEYS[result.outcome])}
                  </Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/**
 * Create the company, or merge it into one of the customers it may already be. Radio buttons,
 * not a select: the reasons are what somebody decides by, and they have to be visible at once.
 */
function DuplicateChoice({
  result,
  chosen,
  disabled,
  onDecide,
}: {
  result: ImportResult
  chosen: string | null
  disabled: boolean
  onDecide: (customerId: string | null) => void
}) {
  const { t } = useTranslation()
  const group = `duplicate-${result.line}`

  return (
    <fieldset className="bg-muted/50 mt-2 grid gap-1.5 rounded-lg border p-2 text-xs">
      <legend className="px-1 font-medium text-amber-600 dark:text-amber-500">
        {t('customerTransfer.duplicates.possible')}
      </legend>
      <label className="flex items-center gap-2">
        <input
          type="radio"
          name={group}
          checked={chosen === null}
          disabled={disabled}
          onChange={() => onDecide(null)}
        />
        {t('customerTransfer.duplicates.createNew')}
      </label>
      {result.candidates.map((candidate) => (
        <label key={candidate.customerId} className="flex items-start gap-2">
          <input
            type="radio"
            name={group}
            className="mt-0.5"
            checked={chosen === candidate.customerId}
            disabled={disabled}
            onChange={() => onDecide(candidate.customerId)}
          />
          <span className="flex flex-col">
            <span>{t('customerTransfer.duplicates.mergeInto', { company: candidate.companyName })}</span>
            <span className="text-muted-foreground">{describe(candidate, t)}</span>
          </span>
        </label>
      ))}
    </fieldset>
  )
}

/** "K-1 · Bielefeld · gleicher Name, gleiche Website" — what tells two records apart, then why they look alike. */
function describe(candidate: DuplicateCandidate, t: ReturnType<typeof useTranslation>['t']): string {
  const reasons = candidate.reasons.map((reason) => t(REASON_LABEL_KEYS[reason])).join(', ')
  return [candidate.customerNumber, candidate.city, reasons].filter(Boolean).join(' · ')
}
